import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Thermometer, FileDown, CalendarClock, TrendingUp, XCircle, Flame } from 'lucide-react';
import { addDays, toDateKey, buildMonthGrid, buildWeekRow, isPastDate, isFutureLockedDate, FUTURE_SLOT_WINDOW_DAYS, formatMonthLabel, formatWeekLabel, formatDayLabel, isSameMonth, weekdayKeyOf } from './dateUtils';
import { getMyGymBookingCalendar } from '../../api/bookingApi';
import { computeEffectiveSlots, bookingCountsForDate } from './useEffectiveSlots';
import { downloadCsv } from '../../lib/exportCsv';
import StatCard from '../StatCard';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import DateEditorPanel from './DateEditorPanel';
import CalendarLegend from './CalendarLegend';

const VIEWS = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

function rangeLabel(view, date) {
  if (view === 'month') return formatMonthLabel(date);
  if (view === 'week') return formatWeekLabel(date);
  return formatDayLabel(date);
}

function shiftDate(view, date, direction) {
  if (view === 'month') return new Date(date.getFullYear(), date.getMonth() + direction, 1);
  if (view === 'week') return addDays(date, direction * 7);
  return addDays(date, direction);
}

// The grid always shows more than just the current month/week/day (month view
// pads leading/trailing days from adjacent months) — fetch bookings for the
// full visible range, not just currentDate's own day.
function visibleRange(view, date) {
  if (view === 'month') {
    const weeks = buildMonthGrid(date);
    return { from: toDateKey(weeks[0][0]), to: toDateKey(weeks[weeks.length - 1][6]) };
  }
  if (view === 'week') {
    const days = buildWeekRow(date);
    return { from: toDateKey(days[0]), to: toDateKey(days[6]) };
  }
  const key = toDateKey(date);
  return { from: key, to: key };
}

// Month-at-a-glance stats, derived from the same weeklySlots/slotOverrides/bookings
// data GymCalendar already holds — no extra API calls.
function computeMonthStats(currentDate, weeklySlots, slotOverrides, bookings) {
  const monthCells = buildMonthGrid(currentDate).flat().filter(d => isSameMonth(d, currentDate));
  let monthBookings = 0;
  let monthCapacity = 0;
  let closedDays = 0;
  let busiestDay = null;
  const rows = [];

  monthCells.forEach(date => {
    const dateKey = toDateKey(date);
    const effective = computeEffectiveSlots(weeklySlots, slotOverrides, dateKey, weekdayKeyOf(date));
    const counts = bookingCountsForDate(bookings, dateKey);
    const dayBooked = Object.values(counts).reduce((a, b) => a + b, 0);
    const dayCapacity = effective.slots.reduce((a, s) => a + (Number(s.capacity) || 0), 0);

    if (effective.isClosed) closedDays += 1;
    monthBookings += dayBooked;
    monthCapacity += dayCapacity;
    if (dayBooked > 0 && (!busiestDay || dayBooked > busiestDay.booked)) busiestDay = { date, booked: dayBooked };

    rows.push({
      date: dateKey,
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      status: effective.isClosed ? 'Closed' : 'Open',
      slots: effective.slots.length,
      booked: dayBooked,
      capacity: dayCapacity,
      occupancy: dayCapacity > 0 ? `${Math.round((dayBooked / dayCapacity) * 100)}%` : '—',
    });
  });

  const avgOccupancy = monthCapacity > 0 ? Math.round((monthBookings / monthCapacity) * 100) : 0;
  return { monthBookings, avgOccupancy, closedDays, busiestDay, rows };
}

export default function GymCalendar({
  weeklySlots, slotOverrides, initialView = 'month', onSaveOverride, onResetOverride, readOnly = false,
}) {
  const [view, setView] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [panelDate, setPanelDate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [heatView, setHeatView] = useState(false);

  // Booking *counts* for the calendar aren't restricted to the short
  // owner-visibility window used by the bookings list page — customers can
  // book up to 14 days out via main-dashboard-stack, and the calendar needs
  // to reflect those even though the owner can't see customer details for
  // dates that far out yet.
  useEffect(() => {
    let cancelled = false;
    const { from, to } = visibleRange(view, currentDate);
    getMyGymBookingCalendar(from, to)
      .then(data => { if (!cancelled) setBookings(Array.isArray(data?.bookings) ? data.bookings : []); })
      .catch(() => { if (!cancelled) setBookings([]); });
    return () => { cancelled = true; };
  }, [view, currentDate]);

  const today = new Date();
  const minNavDate = addDays(today, -FUTURE_SLOT_WINDOW_DAYS);
  const canGoBack = toDateKey(currentDate) > toDateKey(minNavDate);
  const canGoForward = !isFutureLockedDate(currentDate, today);

  function selectDate(date) {
    setPanelDate(date);
  }

  const monthStats = view === 'month' ? computeMonthStats(currentDate, weeklySlots, slotOverrides, bookings) : null;

  function handleExport() {
    if (!monthStats) return;
    downloadCsv(`slots-${formatMonthLabel(currentDate).replace(' ', '-').toLowerCase()}.csv`, [
      { key: 'date', label: 'Date' },
      { key: 'day', label: 'Day' },
      { key: 'status', label: 'Status' },
      { key: 'slots', label: 'Slots' },
      { key: 'booked', label: 'Booked' },
      { key: 'capacity', label: 'Capacity' },
      { key: 'occupancy', label: 'Occupancy' },
    ], monthStats.rows);
  }

  return (
    <div className="space-y-4">
      {view === 'month' && monthStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CalendarClock} label="Bookings This Month" value={monthStats.monthBookings} tone="blue" />
          <StatCard icon={TrendingUp} label="Avg Occupancy" value={`${monthStats.avgOccupancy}%`} tone="green" />
          <StatCard icon={XCircle} label="Closed Days" value={monthStats.closedDays} tone="red" />
          <StatCard
            icon={Flame}
            label="Busiest Day"
            value={monthStats.busiestDay ? monthStats.busiestDay.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
            sublabel={monthStats.busiestDay ? `${monthStats.busiestDay.booked} bookings` : undefined}
            tone="amber"
          />
        </div>
      )}

      <CalendarLegend />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" disabled={!canGoBack} onClick={() => setCurrentDate(d => shiftDate(view, d, -1))}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent cursor-pointer">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setCurrentDate(new Date())}
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] cursor-pointer">
            Today
          </button>
          <button type="button" disabled={!canGoForward} onClick={() => setCurrentDate(d => shiftDate(view, d, 1))}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent cursor-pointer">
            <ChevronRight className="w-4 h-4" />
          </button>
          <p className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] ml-1">{rangeLabel(view, currentDate)}</p>
        </div>

        <div className="flex items-center gap-2 self-start">
          {view === 'month' && (
            <>
              <button
                type="button"
                onClick={() => setHeatView(h => !h)}
                title="Toggle occupancy heatmap"
                className={`p-2 rounded-full transition-colors cursor-pointer ${heatView ? 'bg-amber-50 dark:bg-amber-950/30 text-[#f29900]' : 'text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134]'}`}
              >
                <Thermometer className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleExport}
                title="Export this month's slot summary as CSV"
                className="p-2 rounded-full text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] cursor-pointer"
              >
                <FileDown className="w-4 h-4" />
              </button>
            </>
          )}
          <div className="inline-flex p-1 bg-[#f1f3f4] dark:bg-[#202124] rounded-full">
            {VIEWS.map(v => (
              <button key={v.id} type="button" onClick={() => setView(v.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  view === v.id ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'
                }`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'month' && (
        <MonthView currentDate={currentDate} weeklySlots={weeklySlots} slotOverrides={slotOverrides} bookings={bookings} onSelectDate={selectDate} heatView={heatView} />
      )}
      {view === 'week' && (
        <WeekView currentDate={currentDate} weeklySlots={weeklySlots} slotOverrides={slotOverrides} bookings={bookings} onSelectDate={selectDate} />
      )}
      {view === 'day' && (
        <DayView currentDate={currentDate} weeklySlots={weeklySlots} slotOverrides={slotOverrides} bookings={bookings}
          onSaveOverride={onSaveOverride} onResetOverride={onResetOverride}
          readOnly={readOnly || isPastDate(currentDate) || isFutureLockedDate(currentDate)} />
      )}

      {panelDate && (
        <DateEditorPanel
          key={toDateKey(panelDate)}
          variant="overlay"
          date={panelDate}
          weeklySlots={weeklySlots}
          slotOverrides={slotOverrides}
          bookings={bookings}
          onSaveOverride={onSaveOverride}
          onResetOverride={onResetOverride}
          onClose={() => setPanelDate(null)}
          readOnly={readOnly || isPastDate(panelDate) || isFutureLockedDate(panelDate)}
        />
      )}
    </div>
  );
}
